import { Lightning, Colors } from "@lightningjs/sdk";
import { MediaPoster } from "../../../../components/data-display/MediaPoster/MediaPoster";
import { ChannelItemModel, ProgramModel } from "../../../../lib/models";
import theme from "../../../../lib/theme";
import { Theme, themeManager } from "../../../../components/configurations/Theme/ThemeManager";

interface ChannelItemSpec extends Lightning.Component.TemplateSpec {
    Background: object;
    Content: {
        Logo: {
            Background: object;
            Logo: typeof MediaPoster;
        };
        Info: {
            Top: {
                ChannelName: object;
                ProgramName: object;
            };
            Bottom: {
                Time: object;
                ProgressBar: {
                    Progress: object;
                };
            };
        };
    };
}

export class ChannelItem extends Lightning.Component<ChannelItemSpec> {
    private _channel: ChannelItemModel | null = null;
    private _currentProgram: ProgramModel | null = null;
    private _updateInterval: number | null = null;
    private _themeCallback!: (theme: Theme | undefined) => void;

    static override _template(): Lightning.Component.Template<ChannelItemSpec> {
        return {
            w: theme.channelItem.w,
            h: theme.channelItem.h,
            Background: {
                w: (w) => w,
                h: (h) => h,
                rect: true,
                color: Colors(theme.colors.bg.secondary).get(),
                shader: { type: Lightning.shaders.RoundedRectangle, radius: theme.channelItem.border.radius }
            },
            Content: {
                x: theme.channelItem.padding.x,
                y: theme.channelItem.padding.y,
                w: (w) => w - theme.channelItem.padding.x * 2,
                h: (h) => h - theme.channelItem.padding.y * 2,
                flex: { direction: "row", justifyContent: "space-between", alignContent: "space-between" },
                Logo: {
                    h: (h) => h,
                    w: theme.channelItem.bg.w,
                    Background: {
                        w: (w) => w,
                        h: (h) => h,
                        rect: true,
                        color: Colors(theme.colors.bg.light).alpha(0.9).get(),
                        shader: {
                            type: Lightning.shaders.RoundedRectangle,
                            radius: theme.channelItem.bg.border.radius
                        }
                    },
                    Logo: {
                        x: 16,
                        y: 16,
                        w: theme.channelItem.logo.w,
                        h: theme.channelItem.logo.h,
                        type: MediaPoster,
                        useBorder: false
                    }
                },
                Info: {
                    flexItem: { grow: 1, marginLeft: 30 },
                    flex: { direction: "column" },
                    Top: {
                        y: 4,
                        flex: { direction: "column" },
                        flexItem: { grow: 1 },
                        ChannelName: {
                            text: {
                                text: "",
                                fontSize: theme.channelItem.channelName.fontSize,
                                fontFace: "Bold",
                                textColor: Colors(theme.colors.text.primary).get(),
                                lineHeight: theme.channelItem.channelName.lineHeight,
                                wordWrap: true,
                                wordWrapWidth: theme.channelItem.w - theme.channelItem.logo.w - 80,
                                maxLines: 1,
                                textOverflow: "ellipsis"
                            }
                        },
                        ProgramName: {
                            y: -6,
                            text: {
                                text: "",
                                fontSize: theme.channelItem.programName.fontSize,
                                fontFace: "Regular",
                                textColor: Colors(theme.colors.text.primary).alpha(0.7).get(),
                                lineHeight: theme.channelItem.programName.lineHeight,
                                wordWrap: true,
                                wordWrapWidth: theme.channelItem.w - theme.channelItem.logo.w - 80,
                                maxLines: 1,
                                textOverflow: "ellipsis"
                            }
                        }
                    },
                    Bottom: {
                        y: -6,
                        flex: { direction: "column" },
                        Time: {
                            text: {
                                text: "",
                                fontSize: theme.channelItem.time.fontSize,
                                fontFace: "Regular",
                                textColor: Colors(theme.colors.text.secondary).get(),
                                lineHeight: theme.channelItem.time.lineHeight
                            }
                        },
                        ProgressBar: {
                            w: theme.channelItem.progressBar.w,
                            h: theme.channelItem.progressBar.h,
                            rect: true,
                            color: Colors(theme.colors.bg.secondary).alpha(0.8).get(),
                            shader: {
                                type: Lightning.shaders.RoundedRectangle,
                                radius: 3
                            },
                            Progress: {
                                w: 0,
                                h: theme.channelItem.progressBar.h,
                                rect: true,
                                color: Colors(theme.colors.text.secondary).get(),
                                shader: {
                                    type: Lightning.shaders.RoundedRectangle,
                                    radius: 3
                                }
                            }
                        }
                    }
                }
            }
        };
    }

    set item(channel: ChannelItemModel) {
        this._channel = channel;
        this._updateChannelInfo();
        this._startProgressUpdate();
    }

    private _updateChannelInfo() {
        if (!this._channel) return;

        const { name } = this._channel;
        this._currentProgram = this._getCurrentProgram();
        const programTitle = this._currentProgram?.title ?? "";

        const programTime = this._currentProgram
            ? `${this.formatTime(this._currentProgram.start)} - ${this.formatTime(this._currentProgram.stop)}`
            : "";

        this.patch({
            Content: {
                Logo: {
                    Logo: {
                        data: this._channel
                    }
                },
                Info: {
                    Top: {
                        ChannelName: { text: { text: name } },
                        ProgramName: { text: { text: programTitle } }
                    },
                    Bottom: {
                        Time: { text: { text: programTime } }
                    }
                }
            }
        });

        this._updateProgress();
    }

    private _getCurrentProgram(): ProgramModel | null {
        if (!this._channel || !this._channel.programs?.length) return null;

        const now = new Date();
        const current = this._channel.programs.find((program) => {
            const start = new Date(program.start);
            const end = new Date(program.stop);
            return now >= start && now < end;
        });
        // Match mobile: when nothing is airing right now, fall back to the
        // first program so any channel with EPG still shows something.
        return current ?? this._channel.programs[0];
    }

    private _updateProgress() {
        // Progress only when a program is genuinely airing now; otherwise 0.
        // (Must NOT call _updateChannelInfo here — the displayed program can be
        // a non-airing fallback, which would recurse and freeze the app.)
        let progress = 0;
        const program = this._currentProgram;
        if (program) {
            const now = new Date();
            const start = new Date(program.start);
            const end = new Date(program.stop);
            if (now >= start && now < end) {
                const total = end.getTime() - start.getTime();
                const elapsed = now.getTime() - start.getTime();
                progress = total > 0 ? Math.min(1, Math.max(0, elapsed / total)) : 0;
            }
        }
        this.patch({
            Content: {
                Info: {
                    Bottom: {
                        ProgressBar: {
                            Progress: { w: theme.channelItem.progressBar.w * progress }
                        }
                    }
                }
            }
        });
    }

    private _startProgressUpdate() {
        this._stopProgressUpdate();
        // Re-pick the program + refresh text/progress periodically (handles
        // programs transitioning), without the recursive path above.
        this._updateInterval = setInterval(() => {
            this._updateChannelInfo();
        }, 30000) as unknown as number; // every 30 seconds
    }

    private _stopProgressUpdate() {
        if (this._updateInterval) {
            clearInterval(this._updateInterval);
            this._updateInterval = null;
        }
    }

    private formatTime(dateString: string): string {
        return new Date(dateString).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }

    override _focus() {
        const customTheme = themeManager.getTheme();
        this.patch({
            Background: {
                smooth: { color: customTheme?.interaction ?? Colors(theme.colors.focus).get() }
            }
        });
        this._channel && this.fireAncestors("$onChannelFocus", this._channel);
    }

    override _unfocus() {
        this.patch({
            Background: {
                smooth: { color: Colors(theme.colors.bg.secondary).get() }
            }
        });
    }

    static get width() {
        return theme.channelItem.w;
    }

    static get height() {
        return theme.channelItem.h;
    }

    override _handleEnter() {
        this._channel && this.fireAncestors("$liveChannelSelected", this._channel);
    }

    override _disable() {
        this._stopProgressUpdate();
    }
    override _detach() {
        themeManager.unsubscribe(this._themeCallback);

    }

    override _init() {
        this._themeCallback = this._onThemeChange.bind(this);
        themeManager.subscribe(this._themeCallback);
    
    }

    private _onThemeChange(customTheme: Theme | undefined): void {
        this.patch({
            Content: {
                Info: {
                    Top: {
                        ChannelName: {
                            text: {
                                textColor:  customTheme?.title ?? Colors(theme.colors.text.primary).get()
                            }
                        }
                    }
                }
            }        
        });
      }
}
